import axios, { AxiosResponse } from 'axios';

import {
    FileType,
    ImportJob,
    BulkImportMultiPatchRequest,
    JobAndImportJobID,
    ImportJobRowsErrorsExportResponse,
    ImportJobRow,
    ImportJobRowsSearchRequest,
    ImportJobRowsArgs,
} from 'directory/listings/notification-modals/types';

export const bulkImportMultiPatchAsJob = async (
    tenantId: number,
    request: BulkImportMultiPatchRequest,
): Promise<JobAndImportJobID> => {
    const { data }: AxiosResponse<JobAndImportJobID> = await axios.put(
        `/api/tenants/${tenantId}/import-jobs/bulk-import-multi-patch`,
        { bulkImportRequest: request },
    );

    return data;
};

export const getImportJob = async (
    tenantId: number,
    importJobId: string,
    args?: {
        includeUsers?: boolean;
        includeMapping?: boolean;
        runAsAgencyUser?: boolean;
    },
): Promise<ImportJob> => {
    const params = new URLSearchParams();

    if (args) {
        params.append('args', JSON.stringify(args));
    }

    const url = `/api/tenants/${tenantId}/import-jobs/${importJobId}`;
    const { data }: AxiosResponse<ImportJob> = await axios.get(params.toString() ? `${url}?${params.toString()}` : url);

    return data;
};

export const cancelImportJob = async (
    tenantId: number,
    importJobId: string,
    args?: {
        validationPhase?: boolean;
        commitPhase?: boolean;
    },
): Promise<ImportJob> => {
    const { data }: AxiosResponse<ImportJob> = await axios.put(
        `/api/tenants/${tenantId}/import-jobs/${importJobId}/cancel`,
        { args },
    );

    return data;
};

export const exportImportJobErrors = async (
    agencyId: number,
    tenantId: number,
    importJobId: string,
    showCSVHeaders = false,
): Promise<string> => {
    const payload = {
        agencyId,
        tenantId,
        importJobId,
        importJobRowsErrorsExportRequest: {
            showCSVHeaders,
            searchRequest: {
                filters: [
                    {
                        name: 'importJobRow.importJobID',
                        values: [importJobId],
                    },
                ],
            },
            options: {
                fileType: FileType.CSV,
            },
        },
    };

    const { data }: AxiosResponse<ImportJobRowsErrorsExportResponse> = await axios.put(
        `/api/tenants/${tenantId}/import-jobs/${importJobId}/error-file`,
        payload,
    );

    return data.resource;
};

export const downloadImportJobErrors = async (
    agencyId: number,
    tenantId: number,
    importJobId: string,
): Promise<void> => {
    const fileUrl = await exportImportJobErrors(agencyId, tenantId, importJobId);
    const cleanUrl = fileUrl.replace('/usr/local/media', '');

    window.open(cleanUrl, '_blank');
};

export const batchJob = async (tenantId: number, jobId: string): Promise<void> => {
    await axios.put(`/api/tenants/${tenantId}/jobs/${jobId}/batch`, {
        jobId,
    });
};

export const getImportJobRows = async (
    tenantId: number,
    search: ImportJobRowsSearchRequest,
    args?: ImportJobRowsArgs,
): Promise<ImportJobRow[]> => {
    const params = new URLSearchParams();

    params.append('search', JSON.stringify(search));

    if (args) {
        params.append('args', JSON.stringify(args));
    }

    const { data }: AxiosResponse<ImportJobRow[]> = await axios.get(
        `/api/tenants/${tenantId}/import-job-rows?${params.toString()}`,
    );

    return data || [];
};
